import Box from "@mui/material/Box";
import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";
import { pickVehiclePlateNumber } from "Helpers/pickProfileEntityTitle";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { SelectChangeEvent } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "layout/Loader";
import { VehicleProfileService } from "subModule/src/core/services";
import { useGetVehicleRecommendationsService } from "subModule/src/services/vehicle/useVehicle";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Vehicle } from "../../assets/SideBarIcons/vehicle.svg";
import BackendPaginationTable from "common/Table/TableWithPagination";
import VehicleTabs from "./VehicleTabs";
import ServiceRecommendationSearch from "components/Vehicle/ServiceRecommendationSearch";
interface SearchFormData {
    [key: string]: string | any;
}
export default function VehicleServiceRecommendations() {
    const navigate = useNavigate();
    const params = useParams();
    const vehicleId = params?.id;
    const { t } = useTranslation();
    const [plateNumber, setPlateNumber] = useState("");

    useEffect(() => {
        if (!vehicleId) return;
        let active = true;
        VehicleProfileService.getVehicleByID(+vehicleId)
            .then((res: unknown) => {
                if (!active) return;
                const plate = pickVehiclePlateNumber(res);
                if (plate) setPlateNumber(plate);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [vehicleId]);

    const breadcrumbItems: BreadcrumbItem[] = useMemo(
        () => [
            { labelKey: "vehicles" },
            { labelKey: "vehiclesList", href: "/vehicles" },
            {
                labelKey: "plateNumber",
                ...(plateNumber ? { label: plateNumber } : {}),
                href: vehicleId ? `/vehicle/${vehicleId}` : undefined,
            },
            { labelKey: "serviceRecommendations" },
        ],
        [vehicleId, plateNumber],
    );
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(5);
    const [loading, setLoading] = useState<boolean>(false);
    const [filter, setFilter] = useState<string[]>(["typeName", "type"]);
    const [search, setSearch] = useState<any>({ type: '' });
    const [searchFormData, setSearchFormData] = useState<SearchFormData>({
        typeName: '',
        type: '',
    });
    const {
        data: tableData,
        isFetching,
        refetch,
    } = useGetVehicleRecommendationsService({
        pageNumber: pageNo, size: pageSize, ...search,
        ...(params?.id && { vehicleId: Number(params?.id) }),
    });
    const itemTypes = [
        { label: t('package'), value: '1' },
        { label: t('service'), value: '2' },
        { label: t('part'), value: '3' },
    ];
    const { enqueueSnackbar } = useSnackbar();
    const fetchData = useCallback(() => {
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        }).finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        fetchData();
    }, [pageNo, pageSize, search]);
    useEffect(() => {
        if (loading) handleSubmitSearch();
    }, [searchFormData.type]);
    const handleChangePage = (pageNo: number) => {
        setPageNo(pageNo + 1);
    };
    const handleChangeRows = (pageSize: number) => {
        setPageSize(pageSize);
        setPageNo(1);
    };
    const clearSearch = () => {
        setFilter(["typeName", "type"]);
        setSearchFormData({
            typeName: '',
            type: '',
        });
        setSearch({ typeName: '', type: '', });
    };
    const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
        const { target: { value }, } = event;
        const selectedFilters = typeof value === 'string' ? value.split(',') : value;
        setFilter(selectedFilters);
        setSearchFormData((prevFormData) => {
            const updatedFormData = Object.fromEntries(
                Object.keys(prevFormData).map((key) => [
                    key,
                    value.includes(key)
                        ? prevFormData[key]
                        : typeof prevFormData[key] === "string"
                            ? ""
                            : 0,
                ])
            );
            return updatedFormData;
        });
        if (!value.length && Object.keys(search).length) {
            clearSearch();
        }
    };
    const handleChangeSearch = (field: string, value: any) => {
        const val = (field === 'type') ? (value ? value.value : '') : value;
        if (field === 'type' && searchFormData.type === val) return;
        setSearchFormData((prevData) => ({
            ...prevData,
            [field]: val,
        }));
        if (field === "type") setLoading(true);
    };
    const handleSubmitSearch = () => {
        const filteredSearch: Record<string, string | number | boolean> = {};
        Object.entries(searchFormData).forEach(([key, value]) => {
            if (value) {
                filteredSearch[key] = value?.trim();
            }
        });
        setSearch(filteredSearch);
        setPageNo(1);
        setPageSize(pageSize);
    };
    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/vehicles')}
                onSubmit={() => { }}
                heading={t('vehicles')}
                icon={<Vehicle height={28} width={28} />}
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
                breadcrumbItems={breadcrumbItems}
            />
            <Box className={`flex`}>
                <VehicleTabs params={params} selectedIndex={7} />
                {(isFetching || loading) ? (
                    <Loader />
                ) : (
                    <BackendPaginationTable
                        columns={[
                            { id: 'typeName', label: 'name', numeric: false },
                            { id: 'recommendationType', label: 'type', numeric: false },
                        ]}
                        data={tableData?.pages[0]?.data?.content || []}
                        pageNo={pageNo}
                        pageSize={pageSize}
                        tableData={tableData?.pages[0]?.data || {}}
                        showCheckbox={false}
                        showActions={true}
                        enableSorting={true}
                        showHeader={true}
                        showSearch={true}
                        showFilter={true}
                        showCreateButton={true}
                        actions={[]}
                        handleChangePage={handleChangePage}
                        handleChangeRows={handleChangeRows}
                        loadingSearch={false}
                        rowClick={true}
                        headerComponent={<>
                            <ServiceRecommendationSearch
                                searchFormData={searchFormData}
                                handleChangeFilter={handleChangeFilter}
                                handleChangeSearch={handleChangeSearch}
                                handleSubmitSearch={handleSubmitSearch}
                                clearSearch={clearSearch}
                                {...{ filter, search, itemTypes }}
                            />
                        </>}
                    />
                )}
            </Box>
        </Box>
    );
}
